import * as React from 'react';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import type { Bounds } from '../../Reducers/Configurator/types';
import { mmToCm } from '../../utils/editorUnits';
import { worldToScreen } from '../../utils/textMetrics';
import { XXL_COLORS } from '../../theme/colors';
import type { VisibleRect } from './useEditorViewport';

const RULER_COLOR = XXL_COLORS.rulerAccent;
const GAP_PX = 8;
const RULER_DEPTH_PX = 22;
const STROKE_PX = 1.5;
const LABEL_PX = 12;

type ItemMeasurementsProps = {
  bounds: Bounds | null;
  selectedId: string | null;
  objectCount: number;
  viewRect: VisibleRect;
  pxPerMm: number;
};

export default function ItemMeasurements({
  bounds,
  selectedId,
  objectCount,
  viewRect,
  pxPerMm,
}: ItemMeasurementsProps) {
  if (!bounds || bounds.width <= 0 || bounds.height <= 0) {
    return null;
  }

  if (!selectedId && objectCount === 0) {
    return null;
  }

  if (pxPerMm <= 0) return null;

  const widthCm = mmToCm(bounds.width);
  const heightCm = mmToCm(bounds.height);

  const topLeft = worldToScreen(bounds.x, bounds.y, viewRect, pxPerMm);
  const bottomRight = worldToScreen(
    bounds.x + bounds.width,
    bounds.y + bounds.height,
    viewRect,
    pxPerMm
  );

  const boxLeft = topLeft.x;
  const boxTop = topLeft.y;
  const boxWidth = bottomRight.x - topLeft.x;
  const boxHeight = bottomRight.y - topLeft.y;

  const widthY = boxTop + boxHeight + GAP_PX;
  const heightX = boxLeft - GAP_PX;

  return (
    <g className="no-export" pointerEvents="none">
      <line
        x1={boxLeft}
        y1={widthY}
        x2={boxLeft + boxWidth}
        y2={widthY}
        stroke={RULER_COLOR}
        strokeWidth={STROKE_PX}
      />
      <text
        x={boxLeft + boxWidth / 2}
        y={widthY + RULER_DEPTH_PX - 6}
        fill={RULER_COLOR}
        fontSize={LABEL_PX}
        textAnchor="middle"
        style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
      >
        {t('common.cm', { value: widthCm })}
      </text>

      <line
        x1={heightX}
        y1={boxTop}
        x2={heightX}
        y2={boxTop + boxHeight}
        stroke={RULER_COLOR}
        strokeWidth={STROKE_PX}
      />
      <text
        x={heightX - 6}
        y={boxTop + boxHeight / 2}
        fill={RULER_COLOR}
        fontSize={LABEL_PX}
        textAnchor="end"
        dominantBaseline="middle"
        style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
      >
        {t('common.cm', { value: heightCm })}
      </text>
    </g>
  );
}
